import { useRef } from "react";
import { GraduationCap, Sparkles, Loader2, Calendar, MapPin, Languages, FileSignature, ScrollText, UserCircle, Files, PenLine, Keyboard, Image as ImageIcon, Zap, FileText, X, Trash2, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { PROVINCES, SAMPLES, LANGUAGES, DOC_TYPES } from "../data/samples";
import { fileToResizedDataUrl, rotateDataUrl } from "../lib/imageUtils";

const DOC_ICONS = { PenLine, Keyboard, Image: ImageIcon, Zap, FileText };

export function ContextPanel({
  title, setTitle, year, setYear, region, setRegion, language, setLanguage,
  docType, setDocType, frontImage, onFrontImage, onRemoveFront,
  onTranscribe, loading, canTranscribe, onOpenTeaching, teachingCount,
  onLoadSample, loadingSample,
  profiles, activeProfileId, onSelectProfile, onDeleteProfile,
}) {
  const frontRef = useRef(null);
  const pickFront = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Kies een afbeelding voor de voorkant");
      return;
    }
    try {
      onFrontImage(await fileToResizedDataUrl(file));
      toast.success("Voorkant toegevoegd");
    } catch {
      toast.error("Kon voorkant niet verwerken");
    }
  };
  const rotateFront = async () => {
    try {
      onFrontImage(await rotateDataUrl(frontImage, 90));
    } catch {
      toast.error("Kon voorkant niet draaien");
    }
  };
  return (
    <div className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface)] p-4 shadow-sm sm:p-5">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
        <Field className="md:col-span-3" icon={FileSignature} label="Titel">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Brief van…"
            data-testid="context-title-input"
            className={inputCls}
          />
        </Field>
        <Field className="md:col-span-3" icon={Files} label="Soort document">
          <Select value={docType} onValueChange={setDocType}>
            <SelectTrigger data-testid="context-doctype-select" className="h-10 border-[var(--border-subtle)] bg-[var(--paper)] text-[var(--ink)]">
              <SelectValue placeholder="Kies soort" />
            </SelectTrigger>
            <SelectContent>
              {DOC_TYPES.map((d) => {
                const Icon = DOC_ICONS[d.icon] || FileText;
                return (
                  <SelectItem key={d.value} value={d.value} data-testid={`doctype-option-${d.value}`}>
                    <span className="flex items-center gap-2"><Icon className="h-3.5 w-3.5" /> {d.label}</span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </Field>
        <Field className="md:col-span-2" icon={Calendar} label="Jaartal / periode">
          <input
            value={year}
            onChange={(e) => setYear(e.target.value)}
            placeholder="bijv. 1918"
            data-testid="context-year-input"
            className={inputCls}
          />
        </Field>
        <Field className="md:col-span-2" icon={MapPin} label="Regio / provincie">
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger data-testid="context-region-select" className="h-10 border-[var(--border-subtle)] bg-[var(--paper)] text-[var(--ink)]">
              <SelectValue placeholder="Kies regio" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {PROVINCES.map((p) => (
                <SelectItem key={p} value={p} data-testid={`region-option-${p}`}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field className="md:col-span-2" icon={Languages} label="Taal van de brief">
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger data-testid="context-language-select" className="h-10 border-[var(--border-subtle)] bg-[var(--paper)] text-[var(--ink)]">
              <SelectValue placeholder="Kies taal" />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGES.map((l) => (
                <SelectItem key={l} value={l} data-testid={`language-option-${l}`}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      {docType === "ansichtkaart" && (
        <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-[var(--border-active)] bg-[var(--surface-muted)] p-3" data-testid="postcard-front-row">
          <span className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-[var(--ink-3)]">
            <ImageIcon className="h-3.5 w-3.5" /> Foto voorkant <span className="normal-case tracking-normal text-[var(--ink-3)]/70">(optioneel — wordt niet vertaald)</span>
          </span>
          <input ref={frontRef} type="file" accept="image/*" className="hidden" data-testid="front-image-input" onChange={(e) => pickFront(e.target.files?.[0])} />
          {frontImage ? (
            <div className="flex items-center gap-2">
              <img src={frontImage} alt="Voorkant ansichtkaart" data-testid="front-image-preview" className="h-12 w-16 rounded-md border border-[var(--border-subtle)] object-cover" />
              <button onClick={rotateFront} data-testid="rotate-front-image" title="Voorkant 90° draaien" className="flex items-center gap-1 rounded-md border border-[var(--border-subtle)] px-2 py-1 text-xs text-[var(--ink-2)] hover:border-[var(--brand)] hover:text-[var(--brand)]"><RotateCw className="h-3 w-3" /> Draaien</button>
              <button onClick={onRemoveFront} data-testid="remove-front-image" className="flex items-center gap-1 rounded-md border border-[var(--border-subtle)] px-2 py-1 text-xs text-[var(--ink-2)] hover:bg-[var(--surface)]"><X className="h-3 w-3" /> Verwijderen</button>
            </div>
          ) : (
            <button onClick={() => frontRef.current?.click()} data-testid="add-front-image" className="rounded-md border border-[var(--border-subtle)] bg-[var(--paper)] px-3 py-1.5 text-xs font-medium text-[var(--ink-2)] hover:border-[var(--brand)] hover:text-[var(--brand)]">Voorkant uploaden</button>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-[var(--border-subtle)] pt-4">
        <div className="flex items-center gap-2">
          <UserCircle className="h-4 w-4 text-[var(--ink-3)]" />
          <Select value={activeProfileId || "none"} onValueChange={onSelectProfile}>
            <SelectTrigger data-testid="profile-select" className="h-9 w-48 border-[var(--border-subtle)] bg-[var(--paper)] text-sm text-[var(--ink)]">
              <SelectValue placeholder="Schrijverprofiel" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none" data-testid="profile-option-none">Geen profiel</SelectItem>
              {(profiles || []).map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`profile-option-${p.id}`}>{p.name}</SelectItem>
              ))}
              <SelectItem value="other" data-testid="profile-option-other">Andere schrijver…</SelectItem>
            </SelectContent>
          </Select>
          {activeProfileId && activeProfileId !== "none" && activeProfileId !== "other" && (
            <button
              onClick={() => onDeleteProfile?.(activeProfileId)}
              data-testid="delete-profile-button"
              title="Dit schrijverprofiel verwijderen"
              className="flex h-9 w-9 items-center justify-center rounded-md border border-[var(--border-subtle)] bg-[var(--paper)] text-[var(--ink-3)] transition-colors hover:border-red-400 hover:text-red-500"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>

        <span className="ml-2 hidden font-mono text-[11px] uppercase tracking-wider text-[var(--ink-3)] lg:inline">Voorbeelden:</span>
        {SAMPLES.map((s) => (
          <button
            key={s.id}
            onClick={() => onLoadSample(s)}
            disabled={loadingSample}
            data-testid={`sample-${s.id}`}
            className="flex items-center gap-1.5 rounded-full border border-[var(--border-subtle)] bg-[var(--paper)] px-3 py-1.5 text-xs font-medium text-[var(--ink-2)] transition-colors hover:border-[var(--brand)] hover:text-[var(--brand)] disabled:opacity-50"
          >
            {loadingSample ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ScrollText className="h-3.5 w-3.5" />}
            {s.title}
          </button>
        ))}

        <div className="flex-1" />

        <button
          onClick={onOpenTeaching}
          data-testid="teach-handwriting-btn"
          className="relative flex items-center gap-2 rounded-lg border border-[var(--brand)] px-4 py-2.5 text-sm font-semibold text-[var(--brand)] transition-colors hover:bg-[var(--surface-muted)]"
        >
          <GraduationCap className="h-4 w-4" /> Handschrift leren
          {teachingCount > 0 && (
            <span className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--brand)] px-1 text-[11px] font-bold text-white" data-testid="teach-count-badge">
              {teachingCount}
            </span>
          )}
        </button>

        <button
          onClick={onTranscribe}
          disabled={!canTranscribe || loading}
          data-testid="transcribe-button"
          className="flex items-center gap-2 rounded-lg bg-[var(--brand)] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[var(--brand-hover)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {loading ? "Bezig…" : "Transcriberen"}
        </button>
      </div>
    </div>
  );
}

const inputCls =
  "h-10 w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--paper)] px-3 text-sm text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]";

function Field({ className, icon: Icon, label, children }) {
  return (
    <div className={className}>
      <label className="mb-1.5 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-[var(--ink-3)]">
        <Icon className="h-3.5 w-3.5" /> {label}
      </label>
      {children}
    </div>
  );
}
